import { useCallback } from 'react';
import { ExtraDaysValue } from '~/components/BookingFlow/Overview/ExtraDays';
import { RoomTypeReturnedValue } from '~/components/BookingFlow/Overview/RoomTypes';
import { BookingFlowOverviewContextType } from '~/types/booking-flow-context';

export default function useOverviewFormHooks(formContext: BookingFlowOverviewContextType) {
  const onUpdateRoomTypes = useCallback((value: RoomTypeReturnedValue[]) => {
    if (!formContext.overviewFormValue) {
      return;
    }

    formContext.updateOverviewFormValue({
      ...formContext.overviewFormValue,
      room_types_selected: value,
    }, true);
  }, [formContext]);

  const onUpdateExtraDays = useCallback((value: ExtraDaysValue[]) => {
    if (!formContext.overviewFormValue) {
      return;
    }

    formContext.updateOverviewFormValue({
      ...formContext.overviewFormValue,
      extra_days: value,
    }, true);
  }, [formContext]);

  const onUpdatePrestaySelected = useCallback((packageId: number, selected: boolean) => {
    if (!formContext.overviewFormValue) {
      return;
    }

    const prestaysClone = [...formContext.overviewFormValue?.prestays ?? []].map((prestay) => {
      if (prestay.package_id === packageId) {
        return { ...prestay, selected };
      }

      // When selecting one pre stay, deselect all others (single selection)
      if (selected) {
        return { ...prestay, selected: false };
      }

      return { ...prestay };
    });

    formContext.updateOverviewFormValue({
      ...formContext.overviewFormValue,
      prestays: prestaysClone,
    }, true);
  }, [formContext]);

  const onUpdatePoststaySelected = useCallback((packageId: number, selected: boolean) => {
    if (!formContext.overviewFormValue) {
      return;
    }

    const poststaysClone = [...formContext.overviewFormValue?.poststays ?? []].map((poststay) => {
      if (poststay.package_id === packageId) {
        return { ...poststay, selected };
      }

      // When selecting one post stay, deselect all others (single selection)
      if (selected) {
        return { ...poststay, selected: false };
      }

      return { ...poststay };
    });

    formContext.updateOverviewFormValue({
      ...formContext.overviewFormValue,
      poststays: poststaysClone,
    }, true);
  }, [formContext]);

  const onUpdateOptionQuantity = useCallback((
    optionId: number,
    orderRoomTypeId: number | null,
    quantities: number[],
  ) => {
    if (!formContext.overviewFormValue) {
      return;
    }

    const orderLinesClone = [...formContext.overviewFormValue.order_lines ?? []].map((option) => {
      if (option.id === optionId && option.order_session_room_type_id === orderRoomTypeId) {
        return { ...option, quantities };
      }

      return { ...option };
    });

    formContext.updateOverviewFormValue({
      ...formContext.overviewFormValue,
      order_lines: orderLinesClone,
    }, true);
  }, [formContext]);

  return {
    onUpdateRoomTypes,
    onUpdateExtraDays,
    onUpdatePrestaySelected,
    onUpdatePoststaySelected,
    onUpdateOptionQuantity,
  };
}
